import {InvalidArgumentException} from "../../errors/Exceptions";
import {TmState} from "./TmState.js";
import {isString} from "../../utils.js";


/**
 * Class for Turing machine transition rule
 */
export class TmTransitionRule {
    // (fromState, lookedChar) -> (toState, charToWrite, headDirection)
    #fromState
    #lookedChar // {string} current char, that is looked by TM head
    // ->
    #toState
    #charToWrite // char need to be written instead of lookedChar
    #headDirection // direction, in which TM head need to be moved during applying of transition, in {L, R, eps}

    /**
     *
     * @param fromState {TmState} start state
     * @param lookedChar {string} current char, that is looked by TM head
     * @param toState {TmState} end state
     * @param charToWrite {string} char need to be written instead of lookedChar
     * @param headDirection direction, in which TM head need to be moved during applying of transition.
     * Must be one of following: <code>TmHeadTransitionDirection.LEFT</code>, <code>TmHeadTransitionDirection.RIGHT</code>,
     * <code>TmHeadTransitionDirection.NONE</code>.
     */
    constructor(fromState, lookedChar, toState, charToWrite, headDirection) {
        if(!(fromState instanceof TmState)){
            throw new InvalidArgumentException("fromState must be a valid TmState object");
        }
        if(!(toState instanceof TmState)){
            throw new InvalidArgumentException("toState must be a valid TmState object");
        }

        if(!isString(lookedChar)){
            throw new InvalidArgumentException("lookedChar must be a string");
        }
        if(lookedChar.length !== 1){
            throw new InvalidArgumentException("lookedChar must have length 1");
        }
        if(!isString(charToWrite)){
            throw new InvalidArgumentException("charToWrite must be a string");
        }
        if(charToWrite.length !== 1){
            throw new InvalidArgumentException("charToWrite must have length 1");
        }

        if(!TmHeadTransitionDirection.isValid(headDirection)){
            throw new InvalidArgumentException("headDirection must be a valid TmHeadTransitionDirection");
        }


        this.#fromState = fromState;
        this.#lookedChar = lookedChar;
        this.#toState = toState;
        this.#charToWrite = charToWrite;
        this.#headDirection = headDirection;
    }

    /**
     *
     * @return {TmState}
     */
    get fromState(){
        return this.#fromState;
    }

    /**
     *
     * @return {TmState}
     */
    get toState(){
        return this.#toState;
    }

    /**
     *
     * @return {string}
     */
    get lookedChar(){
        return this.#lookedChar;
    }

    /**
     *
     * @return {string}
     */
    get charToWrite(){
        return this.#charToWrite;
    }

    /**
     *
     * @return {*} one of following: <code>TmHeadTransitionDirection.LEFT</code>, <code>TmHeadTransitionDirection.RIGHT</code>,
     * <code>TmHeadTransitionDirection.NONE</code>.
     */
    get headDirection(){
        return this.#headDirection;
    }

    /**
     * Compares two <code>TmTransitionRule</code> objects (by all properties).
     * @param other {any} any variable to compare
     * @return {boolean}
     */
    equals(other){
        if(!(other instanceof TmTransitionRule)){
            return false;
        }

        if(this === other){
            return true;
        }
        return this.#fromState.equals(other.#fromState) && this.#toState.equals(other.#toState) &&
            this.#lookedChar === other.#lookedChar && this.#charToWrite === other.#charToWrite &&
            this.#headDirection === other.#headDirection;
    }

    /**
     * Returns representation of transition in format of
     * <code>{fromState}{lookedChar} -> {toState}{charToWrite}{headDirection}</code>.
     * @return {string}
     */
    toString(){
        return `{${this.#fromState.toShortString()}}{${this.#lookedChar}} -> ` +
            `{${this.#toState.toShortString()}}{${this.#charToWrite}}` +
            `{${TmHeadTransitionDirection.toShortString(this.#headDirection)}}`;
    }
}



// ENUM of transitions directions of TM head
export const TmHeadTransitionDirection = Object.freeze({
    LEFT: Symbol("LEFT"),
    RIGHT: Symbol("RIGHT"),
    NONE: Symbol("NONE"),


    isValid: (obj) => {
        return obj === TmHeadTransitionDirection.LEFT ||
            obj === TmHeadTransitionDirection.RIGHT ||
            obj === TmHeadTransitionDirection.NONE;
    },

    /**
     *
     * @param direction
     * @return {string}
     */
    toString: (direction) => {
        switch(direction) {
            case TmHeadTransitionDirection.LEFT:
                return "LEFT";
            case TmHeadTransitionDirection.RIGHT:
                return "RIGHT";
            case TmHeadTransitionDirection.NONE:
                return "NONE";
            default:
                throw new InvalidArgumentException("direction must be a valid TmHeadTransitionDirection");
        }
    },

    fromString: (str) => {
        switch(str) {
            case "LEFT":
                return TmHeadTransitionDirection.LEFT;
            case "RIGHT":
                return TmHeadTransitionDirection.RIGHT;
            case "NONE":
                return TmHeadTransitionDirection.NONE;
            default:
                throw new InvalidArgumentException("str must be one from following: LEFT, RIGHT, NONE");
        }
    },

    /**
     * Converts given <code>TmHeadTransitionDirection</code> to short string, that is applied in notation of <code>TmTransition</code>.
     * @param direction is direction ti be converted to string
     * @return {string}
     */
    toShortString(direction){
        switch(direction) {
            case TmHeadTransitionDirection.LEFT:
                return "L";
            case TmHeadTransitionDirection.RIGHT:
                return "R";
            case TmHeadTransitionDirection.NONE:
                return "";
            default:
                throw new InvalidArgumentException("direction must be a valid TmHeadTransitionDirection");
        }
    }
});
